'use client'

import type { PropertyCategoriesBlock as PropertyCategoriesBlockProps } from 'src/payload-types'
import { cn } from '@/utilities/ui'
import React from 'react'
import { Media } from '@/components/Media'
import Link from 'next/link'
import { MoveUpRight } from 'lucide-react'
import Image from 'next/image'
import { useTheme } from '@/providers/Theme'

type Props = {
  className?: string
} & PropertyCategoriesBlockProps

export const PropertyCategoriesBlock = ({
  className,
  heading = 'Homes & Spaces Available',
  propertyTypes = [],
  seeAllButtonLabel = 'See All Properties',
  seeAllButtonLink = '/properties',
  actionCards = [],
}: Props) => {
  const { theme } = useTheme()

  return (
    <div className={cn('pt-5 bg-light !relative !z-10', className)}>
      <div className="container mx-auto px-4">
        {/* Heading */}
        <h2 className="text-typography sm:text-h2 text-mobile-h2 font-semibold mb-5">{heading}</h2>

        {/* Property Type Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mb-5">
          {propertyTypes?.map((property, index) => (
            <Link key={index} href={property.link ?? ''}>
              <div className="rounded-xl overflow-hidden">
                {/* Property Image */}
                {property.image && (
                  <div className="spacesImages rounded-xl h-[260px] overflow-hidden w-full">
                    <Media
                      resource={property.image}
                      className="!w-full h-full object-fill aspect-[16/9] rounded-xl "
                      alt={property.title || ''}
                      loading="eager"
                      priority
                    />
                  </div>
                )}
                {/* Property Content */}
                <div className="pt-4">
                  <h3 className="text-typography text-h4 font-semibold uppercase mb-1">
                    {property.title}
                  </h3>
                  <p className="text-typography text-body text-muted">{property.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* See All Button */}
        <div className="flex justify-center mb-5">
          <Link
            href={seeAllButtonLink}
            className="inline-flex items-center gap-2 h-[55px] px-6 bg-primary-300 text-white rounded-xl hover:bg-primary-200 transition-colors"
          >
            {seeAllButtonLabel}
            <MoveUpRight />
          </Link>
        </div>

        {/* Action Cards */}
        {actionCards?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {actionCards?.map((card, index) => (
              <div
                key={index}
                className="group bg-card md:p-6 p-4 rounded-xl text-center !cursor-pointer hover:[box-shadow:0px_10px_40px_0px_#181A200D] transition-all duration-200"
              >
                {/* Illustration */}
                {theme === 'dark' && card.imageDarkMode ? (
                  // Dark mode image (if provided)
                  <div className="flex justify-center md:mb-6 mb-4 max-md:pt-[40px]">
                    <Media
                      resource={card.imageDarkMode}
                      className="object-contain"
                      alt={card.title || ''}
                      loading="eager"
                      priority
                    />
                  </div>
                ) : card.image ? (
                  // Default / light mode image
                  <div className="flex justify-center md:mb-6 mb-4 max-md:pt-[40px]">
                    <Media
                      resource={card.image}
                      className="object-contain"
                      alt={card.title || ''}
                      loading="eager"
                      priority
                    />
                  </div>
                ) : null}

                {/* Content */}
                <h3 className="text-typography text-h3 font-semibold mb-3">{card.title}</h3>
                <p className="text-typography text-muted mb-6">{card.description}</p>

                {/* Button */}
                {card.buttonLabel && card.buttonLink && (
                  <Link
                    href={card.buttonLink}
                    className="inline-flex items-center rounded-xl h-[55px] px-6 border border-typography text-typography group-hover:bg-primary-200 group-hover:text-white group-hover:border-primary-200  transition-colors gap-2"
                  >
                    {card.buttonLabel}
                    <MoveUpRight />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
